import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Helmet } from 'react-helmet';

import { Box, Button, Container, Drawer, Stack } from '@mui/material';

import { CruisesContract } from '@luxuryescapes/contract-svc-cruise';

import PageHeader from '~/components/Common/Elements/PageHeader';
import { CruiseOffersProvider } from '~/components/Cruises/context/useCruiseOffers';

import { CruiseLine } from '~/services/cruises/CruiseLineService';
import rateCodeService, { GetRateCodes } from '~/services/cruises/RateCodeService';

import CreateRateCodeForm from './components/CreateRateCodeForm';
import RateCodeList from './components/RateCodeList';
import RateCodeManagementSearch from './components/RateCodeManagementSearch';
import { INITIAL_FEEDBACK_STATE } from './constants';
import { CreateRateFormValues, Feedback, SearchValues } from './types';

interface Props {
  disableCreate?: boolean;
  initialVendorFilter?: CruiseLine;
}

function getFilterValue(value: string): boolean | undefined {
  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  return;
}

function RateCodeManagementPage({ disableCreate, initialVendorFilter }: Props) {
  const [total, setTotal] = useState<number>(0);
  const [pagination, setPagination] = useState({ perPage: 10, page: 0 });
  const [createRateDrawer, setCreateRateDrawer] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<Feedback>(INITIAL_FEEDBACK_STATE);
  const [rateCodeList, setRateCodeList] = useState<Array<GetRateCodes>>([]);
  const [searchValues, setSearchValues] = useState<SearchValues>({
    rateCode: '',
    rateCodeDescription: '',
    vendorCode: '',
    enabledFilter: 'all',
    netRateFilter: 'all',
    flashFilter: 'all',
    isInRateGroup: 'all',
    rateGroupIds: [],
  });

  const filters = useMemo(() => {
    return {
      ...(searchValues.rateCode && { rateCodes: searchValues.rateCode }),
      ...(searchValues.rateCodeDescription && { rateCodeDescription: searchValues.rateCodeDescription }),
      ...(searchValues.vendorCode && { vendorCode: searchValues.vendorCode }),
      isEnabled: getFilterValue(searchValues.enabledFilter),
      isNetRate: getFilterValue(searchValues.netRateFilter),
      isFlash: getFilterValue(searchValues.flashFilter),
      isInRateGroup: getFilterValue(searchValues.isInRateGroup),
      rateGroupIds: searchValues.rateGroupIds,
    };
  }, [searchValues]);

  const getList = useCallback(
    async (params?: CruisesContract.RateCodeListQuery) => {
      setFeedback({ loading: true, status: null, message: null });
      // When the initialVendorFilter is provided, we use it to filter the rate codes
      const vendorCode = initialVendorFilter?.code || params?.vendorCode || filters.vendorCode;

      const res = await rateCodeService.getRateCodeList({
        ...filters,
        take: pagination.perPage,
        skip: params?.skip,
        vendorCode,
      });

      if (res?.status === 200) {
        setRateCodeList(res.result);
        setTotal(res.total);
      } else {
        setRateCodeList([]);
      }

      setFeedback({ loading: false });
    },
    [pagination.perPage, filters, initialVendorFilter],
  );

  const handleCreateRateSubmit = useCallback(async (formValues: CreateRateFormValues) => {
    if (!formValues.rateCode || !formValues.vendorId || !formValues.departureIds?.length) {
      setFeedback({
        loading: false,
        status: 'error',
        message: 'Please fill all the fields',
      });
    } else {
      setFeedback({ loading: true, status: null, message: null });

      const res = await rateCodeService.createBulkRateCodeByDepartureId({
        rateCodes: formValues.rateCode.split(','),
        vendorId: formValues.vendorId,
        departureIds: formValues.departureIds,
        isFlash: formValues.isFlash,
        isSelectAll: false,
      });

      if (res?.status === 201) {
        setFeedback({
          loading: false,
          status: 'success',
          message: `Rate code created successfully`,
        });
      } else {
        setFeedback({
          loading: false,
          status: 'error',
          message: 'Something went wrong. Please contact cruises team',
        });
      }
    }
  }, []);

  const handleCreateRateDrawer = useCallback(() => {
    setCreateRateDrawer((prevValue) => !prevValue);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const vendorCode = params.get('vendorCode');
    if (vendorCode) {
      setSearchValues({
        ...searchValues,
        vendorCode: searchValues.vendorCode || vendorCode,
      });
    }
    getList({
      skip: 0,
      take: pagination.perPage,
      vendorCode: vendorCode || '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CruiseOffersProvider>
      <Container maxWidth="xl">
        <Helmet>
          <title>Rate Code Management</title>
        </Helmet>

        <PageHeader title="Rate Code Management" />

        <Box>
          <RateCodeManagementSearch
            onSearch={getList}
            loading={feedback.loading}
            initialVendorFilter={initialVendorFilter}
            searchValues={searchValues}
            setSearchValues={setSearchValues}
          />
        </Box>

        {!disableCreate && (
          <Stack mt={2} width="100%" direction="row" justifyContent="flex-end">
            <Button variant="text" onClick={handleCreateRateDrawer}>
              Create New Rate Code
            </Button>
          </Stack>
        )}

        <Box mt={1}>
          <RateCodeList
            page={pagination.page}
            rateCodes={rateCodeList}
            loading={feedback.loading}
            perPage={pagination.perPage}
            total={total}
            onPaginate={(page) => {
              setPagination({
                ...pagination,
                page,
              });
              getList({
                skip: page * pagination.perPage,
              });
            }}
          />
        </Box>
      </Container>

      <Drawer anchor="right" open={createRateDrawer} onClose={handleCreateRateDrawer}>
        <CreateRateCodeForm feedback={feedback} onClose={handleCreateRateDrawer} onSubmit={handleCreateRateSubmit} />
      </Drawer>
    </CruiseOffersProvider>
  );
}

export default RateCodeManagementPage;
